import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { 
  QrCode, Clock, LogIn, LogOut, Coffee, Play, MapPin, AlertCircle, CheckCircle, Timer, Lock
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

import API from '@/config/api';
// API imported from config;

const TimeClockPage = () => {
  const { user, getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [pinDialogOpen, setPinDialogOpen] = useState(false);
  const [scanDialogOpen, setScanDialogOpen] = useState(false);
  const [pin, setPin] = useState('');
  const [newPin, setNewPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [hasPin, setHasPin] = useState(false);
  const [settingPin, setSettingPin] = useState(false);
  const [selectedAction, setSelectedAction] = useState(null);
  const [gpsStatus, setGpsStatus] = useState({ loading: false, coords: null, error: null });
  const [punching, setPunching] = useState(false);
  const [qrInput, setQrInput] = useState('');
  const [gpsTestResult, setGpsTestResult] = useState(null);
  const [testingGps, setTestingGps] = useState(false);
  const qrInputRef = useRef(null);

  useEffect(() => {
    fetchStatus();
    checkPinStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      const response = await axios.get(`${API}/timeclock/my-status`, getAuthHeaders());
      setStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch status:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkPinStatus = async () => {
    try {
      const response = await axios.get(`${API}/timeclock/my-pin-status`, getAuthHeaders());
      setHasPin(response.data.has_pin);
    } catch (error) {
      console.error('Failed to check PIN status:', error);
    }
  };

  const handleSetPin = async () => {
    if (newPin !== confirmPin) {
      toast.error('PINs do not match');
      return;
    }
    if (newPin.length < 4 || newPin.length > 6 || !/^\d+$/.test(newPin)) {
      toast.error('PIN must be 4-6 digits');
      return;
    }

    setSettingPin(true);
    try {
      await axios.post(`${API}/timeclock/set-pin`, {
        staff_id: user.id,
        pin: newPin
      }, getAuthHeaders());
      toast.success('PIN set successfully!');
      setHasPin(true);
      setPinDialogOpen(false);
      setNewPin('');
      setConfirmPin('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to set PIN');
    } finally {
      setSettingPin(false);
    }
  };

  const getGPSLocation = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation is not supported by your browser'));
        return;
      }

      setGpsStatus({ loading: true, coords: null, error: null });

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const coords = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy
          };
          setGpsStatus({ loading: false, coords, error: null });
          resolve(coords);
        },
        (error) => {
          let errorMsg = 'Unable to get location';
          switch (error.code) {
            case error.PERMISSION_DENIED:
              errorMsg = 'Location permission denied. Please enable location access.';
              break;
            case error.POSITION_UNAVAILABLE:
              errorMsg = 'Location unavailable. Please try again.';
              break;
            case error.TIMEOUT:
              errorMsg = 'Location request timed out. Please try again.';
              break;
          }
          setGpsStatus({ loading: false, coords: null, error: errorMsg });
          reject(new Error(errorMsg));
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        }
      );
    });
  };

  const testGpsLocation = async () => {
    setTestingGps(true);
    setGpsTestResult(null);
    
    try {
      const coords = await getGPSLocation();
      
      const response = await axios.post(`${API}/timeclock/test-gps`, {
        latitude: coords.latitude,
        longitude: coords.longitude,
        accuracy: coords.accuracy  // Send GPS accuracy for diagnostic purposes
      }, getAuthHeaders());
      
      setGpsTestResult(response.data);
      
      if (response.data.success) {
        toast.success(response.data.message);
      } else {
        toast.error(response.data.message || response.data.error);
      }
    } catch (error) {
      if (error.response?.data) {
        setGpsTestResult(error.response.data);
        toast.error(error.response.data.error || error.response.data.detail || 'GPS test failed');
      } else {
        toast.error(error.message || 'Failed to test GPS');
      }
    } finally {
      setTestingGps(false);
    }
  };

  const handlePunchAction = async (action) => {
    if (!hasPin) {
      toast.error('Please set your PIN first');
      setPinDialogOpen(true);
      return;
    }
    setSelectedAction(action);
    setScanDialogOpen(true);
    setPin('');
    setQrInput('');
    
    // Start getting GPS location immediately
    try {
      await getGPSLocation();
    } catch (error) {
      // GPS error will be shown in the dialog
    }
    
    // Focus QR input for scanner
    setTimeout(() => {
      if (qrInputRef.current) {
        qrInputRef.current.focus();
      }
    }, 100);
  };

  const handleScanSubmit = async () => {
    if (!qrInput) {
      toast.error('Please scan the QR code');
      return;
    }
    if (!pin) {
      toast.error('Please enter your PIN');
      return;
    }
    if (!gpsStatus.coords) {
      toast.error('GPS location required. Please enable location access.');
      return;
    }

    setPunching(true);
    try {
      const response = await axios.post(`${API}/timeclock/punch`, {
        qr_code: qrInput,
        pin: pin,
        latitude: gpsStatus.coords.latitude,
        longitude: gpsStatus.coords.longitude,
        punch_type: selectedAction
      }, getAuthHeaders());

      toast.success(response.data.message);
      setScanDialogOpen(false);
      fetchStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to punch');
    } finally {
      setPunching(false);
    }
  };

  const getStatusBadge = () => {
    if (!status) return null;
    
    const badges = {
      not_clocked_in: { label: 'Not Clocked In', variant: 'secondary', icon: LogIn },
      clocked_in: { label: 'Working', variant: 'default', icon: Clock },
      on_break: { label: 'On Break', variant: 'warning', icon: Coffee },
      clocked_out: { label: 'Clocked Out', variant: 'outline', icon: LogOut }
    };
    
    const badge = badges[status.status] || badges.not_clocked_in;
    const Icon = badge.icon;
    
    return (
      <Badge variant={badge.variant} className="text-base px-4 py-2">
        <Icon className="w-4 h-4 mr-2" />
        {badge.label}
      </Badge>
    );
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '-';
    return new Date(timestamp).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getPunchIcon = (type) => {
    const icons = {
      clock_in: <LogIn className="w-4 h-4 text-green-500" />,
      clock_out: <LogOut className="w-4 h-4 text-red-500" />,
      break_start: <Coffee className="w-4 h-4 text-amber-500" />,
      break_end: <Play className="w-4 h-4 text-blue-500" />
    };
    return icons[type] || <Clock className="w-4 h-4" />;
  };

  const getPunchLabel = (type) => {
    const labels = {
      clock_in: 'Clock In',
      clock_out: 'Clock Out',
      break_start: 'Break Start',
      break_end: 'Break End'
    };
    return labels[type] || type;
  };

  if (loading) {
    return (
      <DashboardLayout title="Time Clock">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Time Clock">
      <div className="space-y-6 max-w-4xl mx-auto" data-testid="timeclock-page">
        
        {/* Status Card */}
        <Card className="border-2">
          <CardHeader className="text-center pb-2">
            <CardTitle className="text-2xl font-heading flex items-center justify-center gap-2">
              <Clock className="w-6 h-6 text-primary" />
              Your Status
            </CardTitle>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <div className="flex justify-center">
              {getStatusBadge()}
            </div>
            
            {status && (
              <div className="grid grid-cols-2 gap-4 mt-4">
                <div className="p-4 bg-muted rounded-xl">
                  <p className="text-sm text-muted-foreground">Hours Worked</p>
                  <p className="text-2xl font-bold text-primary">{status.hours_worked || 0}h</p>
                </div>
                <div className="p-4 bg-muted rounded-xl">
                  <p className="text-sm text-muted-foreground">Break Time</p>
                  <p className="text-2xl font-bold text-amber-600">{status.break_hours || 0}h</p>
                </div>
              </div>
            )}

            {/* PIN Setup Prompt */}
            {!hasPin && (
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
                <div className="flex items-center gap-2 text-amber-700">
                  <Lock className="w-5 h-5" />
                  <p className="font-medium">PIN Required</p>
                </div>
                <p className="text-sm text-amber-600 mt-1">You need to set a PIN before using the time clock.</p>
                <Button className="mt-3" onClick={() => setPinDialogOpen(true)} data-testid="set-pin-btn">
                  <Lock className="w-4 h-4 mr-2" />
                  Set Your PIN
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <QrCode className="w-5 h-5 text-primary" />
              Punch Actions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              {status?.next_actions?.includes('clock_in') && (
                <Button
                  size="lg"
                  className="h-24 flex flex-col gap-2 bg-green-600 hover:bg-green-700"
                  onClick={() => handlePunchAction('clock_in')}
                  disabled={!hasPin}
                  data-testid="clock-in-btn"
                >
                  <LogIn className="w-8 h-8" />
                  <span>Clock In</span>
                </Button>
              )}
              
              {status?.next_actions?.includes('break_start') && (
                <Button
                  size="lg"
                  className="h-24 flex flex-col gap-2 bg-amber-500 hover:bg-amber-600"
                  onClick={() => handlePunchAction('break_start')}
                  disabled={!hasPin}
                  data-testid="break-start-btn"
                >
                  <Coffee className="w-8 h-8" />
                  <span>Start Break</span>
                </Button>
              )}
              
              {status?.next_actions?.includes('break_end') && (
                <Button
                  size="lg"
                  className="h-24 flex flex-col gap-2 bg-blue-600 hover:bg-blue-700"
                  onClick={() => handlePunchAction('break_end')}
                  disabled={!hasPin}
                  data-testid="break-end-btn"
                >
                  <Play className="w-8 h-8" />
                  <span>End Break</span>
                </Button>
              )}
              
              {status?.next_actions?.includes('clock_out') && (
                <Button
                  size="lg"
                  className="h-24 flex flex-col gap-2 bg-red-600 hover:bg-red-700"
                  onClick={() => handlePunchAction('clock_out')}
                  disabled={!hasPin}
                  data-testid="clock-out-btn"
                >
                  <LogOut className="w-8 h-8" />
                  <span>Clock Out</span>
                </Button>
              )}
            </div>
            
            {status?.next_actions?.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                <CheckCircle className="w-12 h-12 mx-auto mb-2 text-green-500" />
                <p>You're done for the day!</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Today's Punches */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Timer className="w-5 h-5 text-primary" />
              Today's Activity
            </CardTitle>
          </CardHeader>
          <CardContent>
            {status?.punches?.length > 0 ? (
              <div className="space-y-3">
                {status.punches.map((punch, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                    <div className="flex items-center gap-3">
                      {getPunchIcon(punch.punch_type)}
                      <div>
                        <p className="font-medium">{getPunchLabel(punch.punch_type)}</p>
                        <p className="text-xs text-muted-foreground">
                          {punch.distance_from_center}m from center
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-mono text-lg">{formatTime(punch.timestamp)}</p>
                      {punch.shift_matched && (
                        <Badge variant="outline" className="text-xs">Shift Matched</Badge>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Clock className="w-12 h-12 mx-auto mb-2 opacity-30" />
                <p>No punches recorded today</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* GPS Location Test */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MapPin className="w-5 h-5 text-primary" />
              Test Your Location
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">
              Verify your GPS is working and you're within range of the center before punching.
            </p>
            
            <Button 
              onClick={testGpsLocation} 
              disabled={testingGps}
              variant="outline"
              className="w-full"
              data-testid="test-gps-btn"
            >
              {testingGps ? (
                <>Testing...</>
              ) : (
                <>
                  <MapPin className="w-4 h-4 mr-2" />
                  Test GPS Location
                </>
              )}
            </Button>
            
            {gpsTestResult && (
              <div className={`mt-4 p-4 rounded-lg ${
                gpsTestResult.success 
                  ? 'bg-green-50 border border-green-200' 
                  : gpsTestResult.center_configured === false
                    ? 'bg-amber-50 border border-amber-200'
                    : 'bg-red-50 border border-red-200'
              }`}>
                <div className="flex items-center gap-2 mb-2">
                  {gpsTestResult.success ? (
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  ) : (
                    <AlertCircle className="w-5 h-5 text-red-600" />
                  )}
                  <span className="font-medium">
                    {gpsTestResult.success ? 'Location OK!' : gpsTestResult.error || 'Outside range'}
                  </span>
                </div>
                
                {gpsTestResult.distance_meters !== undefined && (
                  <div className="text-sm space-y-1">
                    <p>Distance from center: <strong>{gpsTestResult.distance_meters}m</strong></p>
                    <p>Allowed radius: <strong>{gpsTestResult.allowed_radius}m</strong></p>
                    {gpsTestResult.your_location?.accuracy_meters && (
                      <p>GPS accuracy: <strong>±{Math.round(gpsTestResult.your_location.accuracy_meters)}m</strong></p>
                    )}
                    {!gpsTestResult.success && gpsTestResult.center_configured && (
                      <p className="text-red-600">
                        You need to be {Math.round(gpsTestResult.distance_meters - gpsTestResult.allowed_radius)}m closer to punch in.
                      </p>
                    )}
                  </div>
                )}
                
                {/* Diagnostic hints for troubleshooting */}
                {gpsTestResult.diagnostic_hints && gpsTestResult.diagnostic_hints.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <p className="text-xs font-medium text-gray-600 mb-1">Troubleshooting:</p>
                    <ul className="text-xs text-gray-600 space-y-1">
                      {gpsTestResult.diagnostic_hints.map((hint, idx) => (
                        <li key={idx}>{hint}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Set PIN Dialog */}
        <Dialog open={pinDialogOpen} onOpenChange={setPinDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Lock className="w-5 h-5" />
                {hasPin ? 'Change Your PIN' : 'Set Your PIN'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>New PIN (4-6 digits)</Label>
                <Input
                  type="password"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  value={newPin}
                  onChange={(e) => setNewPin(e.target.value.replace(/\D/g, ''))}
                  placeholder="Enter PIN"
                  data-testid="new-pin-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Confirm PIN</Label>
                <Input
                  type="password"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  value={confirmPin}
                  onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, ''))}
                  placeholder="Confirm PIN"
                  data-testid="confirm-pin-input"
                />
              </div>
              <Button 
                className="w-full" 
                onClick={handleSetPin}
                disabled={settingPin || !newPin || !confirmPin}
                data-testid="save-pin-btn"
              >
                {settingPin ? 'Saving...' : 'Save PIN'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Scan & Punch Dialog */}
        <Dialog open={scanDialogOpen} onOpenChange={setScanDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <QrCode className="w-5 h-5" />
                {getPunchLabel(selectedAction)}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              {/* GPS Status */}
              <div className={`p-3 rounded-lg flex items-center gap-2 ${
                gpsStatus.loading ? 'bg-blue-50 text-blue-700' :
                gpsStatus.coords ? 'bg-green-50 text-green-700' :
                'bg-red-50 text-red-700'
              }`}>
                <MapPin className="w-5 h-5" />
                {gpsStatus.loading ? (
                  <span>Getting your location...</span>
                ) : gpsStatus.coords ? (
                  <span>Location acquired (accuracy: {Math.round(gpsStatus.coords.accuracy)}m)</span>
                ) : (
                  <span>{gpsStatus.error || 'Location required'}</span>
                )}
                {!gpsStatus.loading && !gpsStatus.coords && (
                  <Button size="sm" variant="outline" onClick={getGPSLocation}>
                    Retry
                  </Button>
                )}
              </div>

              {/* QR Code Input */}
              <div className="space-y-2">
                <Label>Scan QR Code</Label>
                <div className="relative">
                  <Input
                    ref={qrInputRef}
                    value={qrInput}
                    onChange={(e) => setQrInput(e.target.value)}
                    placeholder="Point scanner at QR code..."
                    className="pr-10"
                    data-testid="qr-input"
                  />
                  <QrCode className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                </div>
                {qrInput && (
                  <div className="flex items-center gap-1 text-green-600 text-sm">
                    <CheckCircle className="w-4 h-4" />
                    QR Code scanned
                  </div>
                )}
              </div>

              {/* PIN Input */}
              <div className="space-y-2">
                <Label>Enter Your PIN</Label>
                <Input
                  type="password"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  value={pin}
                  onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
                  placeholder="Your PIN"
                  data-testid="punch-pin-input"
                />
              </div>

              <Button 
                className="w-full h-12 text-lg" 
                onClick={handleScanSubmit}
                disabled={punching || !qrInput || !pin || !gpsStatus.coords}
                data-testid="confirm-punch-btn"
              >
                {punching ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-current mr-2"></div>
                    Processing...
                  </>
                ) : (
                  <>Confirm {getPunchLabel(selectedAction)}</>
                )}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default TimeClockPage;
